import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Plus, Check, Trash2, Euro } from "lucide-react";

const MONTHS = [
  "Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno",
  "Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre",
];

export default function Pagamenti() {
  const { user } = useAuth();
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState({ client_name: "", amount: "", info: "", rata: "" });

  const isAdmin = user?.role === "admin";
  const years = useMemo(() => {
    const cur = now.getFullYear();
    return [cur - 1, cur, cur + 1, cur + 2];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/payments`, { params: { month, year } });
      setRows(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore caricamento");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month, year]);

  if (user && !isAdmin) return <Navigate to="/" replace />;

  const addRow = async () => {
    if (!draft.client_name.trim()) return toast.error("Cliente richiesto");
    const amount = parseFloat(draft.amount || "0") || 0;
    try {
      const { data } = await api.post(`/payments`, {
        client_name: draft.client_name.trim(),
        amount,
        info: draft.info,
        rata: draft.rata,
        paid: false,
        month,
        year,
      });
      setRows((r) => [...r, data]);
      setDraft({ client_name: "", amount: "", info: "", rata: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  const patch = async (p, patchObj) => {
    try {
      const { data } = await api.patch(`/payments/${p.id}`, patchObj);
      setRows((r) => r.map((x) => (x.id === p.id ? data : x)));
    } catch (e) {
      toast.error("Errore");
    }
  };

  const removeRow = async (p) => {
    if (!confirm(`Eliminare pagamento di ${p.client_name}?`)) return;
    try {
      await api.delete(`/payments/${p.id}`);
      setRows((r) => r.filter((x) => x.id !== p.id));
    } catch { toast.error("Errore"); }
  };

  const total = rows.reduce((s, r) => s + (r.amount || 0), 0);
  const paid = rows.filter((r) => r.paid).reduce((s, r) => s + (r.amount || 0), 0);
  const remaining = total - paid;

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="pagamenti-page">
      <div className="flex items-end gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-fuchsia-400/80">Cash flow</div>
          <h1 className="text-3xl font-semibold tracking-tight text-white flex items-center gap-2">
            <Euro size={22} className="text-fuchsia-400" /> Pagamenti
          </h1>
        </div>
        <div className="ml-auto flex gap-2 flex-wrap">
          <select
            data-testid="pag-month-select"
            value={month}
            onChange={(e) => setMonth(parseInt(e.target.value))}
            className="h-10 rounded-lg bg-white/5 border border-white/10 text-white text-sm px-3"
          >
            {MONTHS.map((m, i) => <option key={i} value={i + 1} className="bg-zinc-900">{m}</option>)}
          </select>
          <select
            data-testid="pag-year-select"
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value))}
            className="h-10 rounded-lg bg-white/5 border border-white/10 text-white text-sm px-3"
          >
            {years.map((y) => <option key={y} value={y} className="bg-zinc-900">{y}</option>)}
          </select>
        </div>
      </div>

      <section className="tp-glass rounded-3xl overflow-hidden">
        <div className="px-5 py-4 border-b border-white/5 flex items-center gap-3">
          <h2 className="text-base font-semibold text-white">
            {MONTHS[month - 1]} {year}
          </h2>
          <span className="ml-auto text-xs text-zinc-500 tabular-nums">{rows.length} voci</span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-widest text-zinc-500">
              <tr className="border-b border-white/5">
                <th className="text-left px-4 py-3">Cliente</th>
                <th className="text-left px-4 py-3">Importo (€)</th>
                <th className="text-left px-4 py-3">Info</th>
                <th className="text-left px-4 py-3">Rata</th>
                <th className="text-center px-4 py-3">Pagato</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={6} className="text-center py-8 text-zinc-500">Caricamento…</td></tr>
              )}
              {!loading && rows.map((r) => (
                <tr key={r.id} data-testid={`pag-row-${r.id}`} className={`border-b border-white/5 hover:bg-white/[0.02] ${r.paid ? "opacity-60" : ""}`}>
                  <td className="px-4 py-2">
                    <Input value={r.client_name} onChange={(e) => patch(r, { client_name: e.target.value })} className="bg-transparent border-0 h-8 text-white p-0 focus-visible:ring-0" />
                  </td>
                  <td className="px-4 py-2 w-32">
                    <Input type="number" step="0.01" value={r.amount} onChange={(e) => patch(r, { amount: parseFloat(e.target.value) || 0 })} className="bg-transparent border-0 h-8 text-white p-0 focus-visible:ring-0 tabular-nums" />
                  </td>
                  <td className="px-4 py-2">
                    <Input value={r.info || ""} onChange={(e) => patch(r, { info: e.target.value })} className="bg-transparent border-0 h-8 text-white p-0 focus-visible:ring-0" placeholder="—" />
                  </td>
                  <td className="px-4 py-2 w-24">
                    <Input value={r.rata || ""} onChange={(e) => patch(r, { rata: e.target.value })} className="bg-transparent border-0 h-8 text-white p-0 focus-visible:ring-0" placeholder="—" />
                  </td>
                  <td className="px-4 py-2 text-center">
                    <button
                      data-testid={`pag-paid-toggle-${r.id}`}
                      onClick={() => patch(r, { paid: !r.paid })}
                      title={r.paid ? "Segna come non pagato" : "Segna come pagato"}
                      className={`h-8 w-8 inline-flex items-center justify-center rounded-full transition-colors ${
                        r.paid ? "bg-emerald-500 text-white" : "bg-white/5 border border-white/10 text-zinc-400 hover:text-white"
                      }`}
                    >
                      <Check size={16} strokeWidth={3} />
                    </button>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button onClick={() => removeRow(r)} data-testid={`pag-delete-${r.id}`} className="p-2 text-zinc-500 hover:text-red-400 rounded-md hover:bg-red-500/10">
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
              <tr className="bg-white/[0.03]" data-testid="pag-add-row">
                <td className="px-4 py-2">
                  <Input placeholder="Nuovo cliente…" data-testid="pag-new-client" value={draft.client_name} onChange={(e) => setDraft({ ...draft, client_name: e.target.value })} className="bg-transparent border-0 h-9 text-white p-0 focus-visible:ring-0" />
                </td>
                <td className="px-4 py-2">
                  <Input type="number" step="0.01" placeholder="0,00" data-testid="pag-new-amount" value={draft.amount} onChange={(e) => setDraft({ ...draft, amount: e.target.value })} className="bg-transparent border-0 h-9 text-white p-0 focus-visible:ring-0 tabular-nums" />
                </td>
                <td className="px-4 py-2">
                  <Input placeholder="Note" data-testid="pag-new-info" value={draft.info} onChange={(e) => setDraft({ ...draft, info: e.target.value })} className="bg-transparent border-0 h-9 text-white p-0 focus-visible:ring-0" />
                </td>
                <td className="px-4 py-2">
                  <Input placeholder="1/3" data-testid="pag-new-rata" value={draft.rata} onChange={(e) => setDraft({ ...draft, rata: e.target.value })} className="bg-transparent border-0 h-9 text-white p-0 focus-visible:ring-0" />
                </td>
                <td className="px-4 py-2 text-center">
                  <Button onClick={addRow} data-testid="pag-add-button" size="sm" className="tp-brand-btn h-8 rounded-full px-3 text-xs">
                    <Plus size={12} className="mr-1" /> Aggiungi
                  </Button>
                </td>
                <td />
              </tr>
            </tbody>
            <tfoot>
              <tr className="border-t border-white/10 bg-white/[0.02]">
                <td className="px-4 py-3 text-[11px] uppercase tracking-widest text-zinc-500" colSpan={2}>Totale mensile</td>
                <td colSpan={4} className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-6 tabular-nums">
                    <span className="text-emerald-300 text-sm">Pagato € {paid.toFixed(2)}</span>
                    <span className="text-amber-300 text-sm">Rimanente € {remaining.toFixed(2)}</span>
                    <span data-testid="pag-total" className="text-white text-lg font-semibold">
                      € {total.toFixed(2)}
                    </span>
                  </div>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </section>
    </div>
  );
}
